import {
  computeMetricPercentDelta,
  computeTopPercentCeiling,
} from "./productivityComparison.js";
import { MIN_SESSION_COHORT_SIZE } from "./sessionComparisonConstants.js";

export type SessionComparisonMetricKey =
  | "points"
  | "durationMinutes"
  | "tasksCompleted"
  | "distractionMinutes";

/** Rank placement for one metric within the day's qualifying session cohort. */
export interface SessionMetricRank {
  rank: number;
  totalRanked: number;
  /** Upper bound of top X% (1 = best). Lower is better placement. */
  topPercentCeiling: number;
  /** Upper bound of bottom X% (100 = worst). Higher is worse placement. */
  bottomPercentCeiling: number;
}

export type SessionComparisonRanks = Record<
  SessionComparisonMetricKey,
  SessionMetricRank
>;

export interface SessionComparisonMetrics {
  points: number;
  durationMinutes: number;
  tasksCompleted: number;
  distractionMinutes: number;
}

export interface SessionCohortAverages {
  sessionCount: number;
  avgPoints: number;
  avgDurationMinutes: number;
  avgTasksCompleted: number;
  avgDistractionMinutes: number;
}

export interface SessionComparisonDeltas {
  pointsVsAvg: number;
  pointsVsAvgPercent: number | null;
  durationMinutesVsAvg: number;
  durationMinutesVsAvgPercent: number | null;
  tasksCompletedVsAvg: number;
  tasksCompletedVsAvgPercent: number | null;
  distractionMinutesVsAvg: number;
  distractionMinutesVsAvgPercent: number | null;
}

export interface SessionComparisonForSession {
  sessionId: string;
  periodYmd: string;
  user: SessionComparisonMetrics;
  cohort: SessionCohortAverages;
  deltas: SessionComparisonDeltas;
  /** Per-metric cohort ranks; present when cohort meets minimum size. */
  ranks: SessionComparisonRanks;
}

export function meetsMinimumSessionCohortSize(
  sessionCount: number,
  min: number = MIN_SESSION_COHORT_SIZE
): boolean {
  return sessionCount >= min;
}

/**
 * Precomputed deltas between a session and cohort session averages.
 */
export function computeSessionComparisonDeltas(
  user: SessionComparisonMetrics,
  cohort: SessionCohortAverages
): SessionComparisonDeltas {
  const pointsVsAvg = user.points - cohort.avgPoints;
  const durationMinutesVsAvg = user.durationMinutes - cohort.avgDurationMinutes;
  const tasksCompletedVsAvg = user.tasksCompleted - cohort.avgTasksCompleted;
  const distractionMinutesVsAvg =
    user.distractionMinutes - cohort.avgDistractionMinutes;

  return {
    pointsVsAvg,
    pointsVsAvgPercent: computeMetricPercentDelta(user.points, cohort.avgPoints),
    durationMinutesVsAvg,
    durationMinutesVsAvgPercent: computeMetricPercentDelta(
      user.durationMinutes,
      cohort.avgDurationMinutes
    ),
    tasksCompletedVsAvg,
    tasksCompletedVsAvgPercent: computeMetricPercentDelta(
      user.tasksCompleted,
      cohort.avgTasksCompleted
    ),
    distractionMinutesVsAvg,
    distractionMinutesVsAvgPercent: computeMetricPercentDelta(
      user.distractionMinutes,
      cohort.avgDistractionMinutes
    ),
  };
}

/**
 * Builds rank placement objects from SQL rank columns.
 */
export function buildSessionMetricRank(
  rank: number,
  totalRanked: number
): SessionMetricRank {
  const safeRank = Math.max(1, rank);
  const safeTotal = Math.max(1, totalRanked);
  const topPercentCeiling = computeTopPercentCeiling(safeRank, safeTotal);
  const bottomPercentCeiling = Math.max(
    1,
    Math.min(100, Math.ceil((100 * (safeTotal - safeRank + 1)) / safeTotal))
  );
  return {
    rank: safeRank,
    totalRanked: safeTotal,
    topPercentCeiling,
    bottomPercentCeiling,
  };
}

/**
 * Builds a full comparison result for one session row + shared cohort stats.
 */
export function buildSessionComparisonResult(input: {
  sessionId: string;
  periodYmd: string;
  user: SessionComparisonMetrics;
  cohort: SessionCohortAverages;
  ranks: SessionComparisonRanks;
}): SessionComparisonForSession {
  return {
    sessionId: input.sessionId,
    periodYmd: input.periodYmd,
    user: input.user,
    cohort: input.cohort,
    deltas: computeSessionComparisonDeltas(input.user, input.cohort),
    ranks: input.ranks,
  };
}
